/**
 * The program lens: one stage-scoped view over activities, inspections and ITPs.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * THE MAPPING RULE AND ITS DECLARED LIMIT
 *   Each program is a stage-scoped lens (the direction document, section 1): the
 *   verification types a stage owns should narrow the slice, client-side, through the
 *   taxonomies read model. The Phase 1 payload does not carry the join: a verification
 *   type row carries `stage_id`, the stage register carries no row identifier, and the
 *   demo tenant's verification types carry no stage at all. Per the dispatch, the
 *   mapping is recorded as underivable in NOTES.md and the registers below render
 *   UNFILTERED rather than on an invented mapping. When the payload grows the join,
 *   `stageTypeTokens` is the one seam that changes.
 *
 * The activities are a date-bounded fetch (GET /api/activities with from_date and
 * to_date); the inspections and the ITPs are the same reads the registers use.
 */

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ApiError, apiUpload } from "../api/client";
import { CreateScheduleDialog } from "../screens/CreateScheduleDialog";
import { FilePicker } from "@/components/ui/file-picker";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import {
  listCalendarActivities,
  listCalendarPeriods,
  type CalendarActivity,
} from "../api/calendar";
import { listRequestTargets, requestInspection, type RequestTarget } from "../api/conduct";
import { listRfis, type RfiSummary } from "../api/rfis";
import { listPlanningProjects, type Activity } from "../api/schedules";
import { CalendarPlanDialog } from "../calendar/CalendarPage";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { PICKER_CLASS, PickerOption, pickerWidth } from "./PickerOption";
import { AttentionLegend } from "../calendar/model";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog, ToastBar } from "./ActionDialog";
import { getContract, getTaxonomies, type TaxonomyToken } from "../api/portfolio";
import { listItps, type ItpSummary } from "../api/itps";
import { listVerifications, type VerificationSummary } from "../api/verifications";
import { AbsencePanel, EmptyState, LoadingLine, StateChip, day } from "../screens/parts";
import { useQuery } from "../screens/useQuery";
import { YearGrid } from "../screens/YearGrid";

function asScheduleActivity(activity: CalendarActivity): Activity {
  return {
    id: activity.id,
    humanId: activity.humanId,
    scheduleId: activity.scheduleId,
    scheduleWorkflowState: "",
    plannedDate: activity.plannedDate,
    plannedEndDate: activity.plannedEndDate,
    status: activity.status,
    isTerminal: false,
    priority: activity.priority,
    purpose: activity.purpose,
    checklistReference: null,
    contractorPartyId: activity.contractorPartyId,
    contractorPartyName: activity.contractorPartyName,
    contractId: null,
    verificationTypeCode: activity.verificationTypeCode,
    verificationTypeLabel: activity.verificationTypeLabel,
    assessorPersonIds: [],
    assessorDisplayNames: [...activity.assessorDisplayNames],
    negotiationState: activity.negotiationState,
    pendingDate: activity.pendingDate,
    availableMoves: [],
    availableEvents: [],
    inspectionId: null,
    mayConductInspection: false,
  };
}

/**
 * The four lenses (M5 P6 seat X #1): each program shows ITS stage's records, so the
 * four routes stop being identical screens under four titles.
 *
 * THE SCOPING RULE AND ITS SEAMS
 *   The stage stamp lives on the VERIFICATION TYPE (the demo seed stamps each type's
 *   stage_code), not on the record rows, and neither the taxonomies payload nor the
 *   activity feed carries that join. So each lens narrows client-side on the types its
 *   stage owns: the activity rows carry `verificationTypeCode` (exact codes below) and
 *   the inspection summaries carry `verificationTypeLabel` (the same types' labels).
 *   The ITP register carries no stage axis at all (discipline only), so its rows stay
 *   unfiltered — the lens description names what the lens covers. When any payload
 *   grows the stage join, these two maps are the seams that change.
 */
export type ProgramLensKey = "engineering" | "procurement" | "construction" | "externalAudits";

/**
 * THE THIRD SOURCE (Mahmoud's eighth addendum, 2026-10-11): from under the engineering,
 * procurement and construction programs the operator initiates an inspection request,
 * sent to the contractor with a proposed date. The program the request is sent from is
 * its verification type, which decides its checklist at conduct. The audits lens sends
 * none.
 */
export const REQUEST_PROGRAM: Partial<Record<ProgramLensKey, string>> = {
  engineering: "ENGINEERING_ASSESSMENT",
  procurement: "PROCUREMENT_SURVEILLANCE",
  construction: "CONSTRUCTION_SURVEILLANCE",
};

/** The activity feed's verification-type codes each lens's stage owns. */
const LENS_ACTIVITY_TYPE_CODES: Record<ProgramLensKey, readonly string[]> = {
  engineering: ["ENGINEERING_ASSESSMENT"],
  procurement: ["PROCUREMENT_SURVEILLANCE"],
  construction: ["CONSTRUCTION_SURVEILLANCE"],
  externalAudits: ["QUALITY_AUDIT"],
};

/** The inspection summaries' verification-type labels each lens's stage owns. */
const LENS_INSPECTION_TYPE_LABELS: Record<ProgramLensKey, readonly string[]> = {
  engineering: ["Engineering assessment"],
  procurement: ["Procurement surveillance"],
  construction: ["Construction surveillance"],
  externalAudits: ["Quality audit"],
};

export interface ProgramLensProps {
  /** The program's page title; the stage name the program is scoped to. */
  readonly title: string;
  /** Which of the four stage lenses this route renders. */
  readonly lens: ProgramLensKey;
}

export function ProgramLens({ title, lens }: ProgramLensProps): React.JSX.Element {
  const token = useToken();
  const taxonomies = useQuery(() => getTaxonomies(token), [token]);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [showShutdown, setShowShutdown] = useState(true);
  const [showRamadan, setShowRamadan] = useState(true);
  const [reload, setReload] = useState(0);
  const periods = useQuery((signal) => listCalendarPeriods(token, signal), [token]);
  const activities = useQuery(
    (signal) =>
      listCalendarActivities(token, {
        fromDate: `${String(year)}-01-01`,
        toDate: `${String(year)}-12-31`,
        limit: 500,
        signal,
      }),
    [token, year, reload],
  );
  const inspections = useQuery(() => listVerifications(token, {}), [token]);
  const itps = useQuery(() => listItps(token, {}), [token]);
  const planningProjects = useQuery((signal) => listPlanningProjects(token, signal), [token]);
  const mayPlan = planningProjects.kind === "ready" && planningProjects.data.length > 0;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const actParam = searchParams.get("act");
  const [planning, setPlanning] = useState(false);
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  // Wave Experience A: the quick action's ?act=request opens the dialog every time it is
  // followed (the URL says so, not a state read once at mount), and closing drops it.
  const [requestingByHand, setRequesting] = useState(false);
  const requesting = requestingByHand || actParam === "request";
  const [toast, setToast] = useState<string | null>(null);
  const program = REQUEST_PROGRAM[lens] ?? null;
  // The program's own requests (both sources of the date loop), and the contracts this
  // caller may send one on: the initiate control appears only when there is one.
  const requests = useQuery(
    (signal) =>
      program === null
        ? Promise.resolve({ total: 0, items: [] as RfiSummary[] })
        : listRfis(token, { verificationTypeCode: program, signal }),
    [token, program, reload],
  );
  const targets = useQuery(
    (signal) =>
      program === null ? Promise.resolve([] as RequestTarget[]) : listRequestTargets(token, signal),
    [token, program],
  );
  const mayRequest = program !== null && targets.kind === "ready" && targets.data.length > 0;

  // The lens's stage scope, applied client-side on the axes the rows actually carry.
  const typeCodes = LENS_ACTIVITY_TYPE_CODES[lens];
  const activitiesInLens =
    activities.kind === "ready"
      ? activities.data.items.filter((row) => typeCodes.includes(row.verificationTypeCode))
      : [];
  const typeLabels = LENS_INSPECTION_TYPE_LABELS[lens];
  const inspectionsInLens =
    inspections.kind === "ready"
      ? inspections.data.items.filter((record) => typeLabels.includes(record.verificationTypeLabel))
      : [];

  return (
    <>
      <h2 className="q-shell__heading" data-testid="program-title">
        {title}
      </h2>
      <p className="q-shell__lede" data-testid="program-lens-description">
        {strings.programs.lenses[lens]}
      </p>
      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}

      <div className="q-section-bands" data-testid="program-hub-sections">
        <section className="q-section" data-testid="program-calendar-section">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="q-section__heading m-0">{strings.programs.calendarHeading}</h3>
              {(mayPlan || mayRequest) && (
                <div className="q-actions-row m-0" data-testid="program-actions">
                  {mayPlan && (
                    <>
                      <button
                        type="button"
                        className="q-button q-mini--primary"
                        data-testid="program-plan-open"
                        onClick={() => {
                          setPlanning(true);
                        }}
                      >
                        {strings.calendar.planActivity}
                      </button>
                      <button
                        type="button"
                        className="q-button q-mini--primary"
                        data-testid="program-create-schedule-open"
                        onClick={() => {
                          setCreatingSchedule(true);
                        }}
                      >
                        {strings.schedules.createSchedule}
                      </button>
                    </>
                  )}
                  {mayRequest && (
                    <button
                      type="button"
                      className="q-button q-mini--primary"
                      data-testid="program-request-open"
                      onClick={() => {
                        setRequesting(true);
                      }}
                    >
                      {strings.programs.requestOpen}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="program-year-prev"
                  className="q-button q-button--mini q-button--quiet"
                  aria-label={strings.calendar.previous}
                  onClick={() => {
                    setYear((y) => y - 1);
                  }}
                >
                  ←
                </button>
                <span className="font-semibold tabular-nums" data-testid="program-year-display">
                  {year}
                </span>
                <button
                  type="button"
                  data-testid="program-year-next"
                  className="q-button q-button--mini q-button--quiet"
                  aria-label={strings.calendar.next}
                  onClick={() => {
                    setYear((y) => y + 1);
                  }}
                >
                  →
                </button>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showShutdown}
                    onChange={(event) => {
                      setShowShutdown(event.target.checked);
                    }}
                  />
                  <span>{strings.schedules.showShutdown}</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showRamadan}
                    onChange={(event) => {
                      setShowRamadan(event.target.checked);
                    }}
                  />
                  <span>{strings.schedules.showRamadan}</span>
                </label>
              </div>
            </div>
          </div>
          <AttentionLegend />
          <div data-testid="program-yeargrid-scroll">
            <YearGrid
              year={year}
              activities={activitiesInLens.map(asScheduleActivity)}
              rfis={requests.kind === "ready" ? requests.data.items : []}
              periods={
                periods.kind === "ready" ? periods.data.map((one) => ({ ...one, note: null })) : []
              }
              showShutdown={showShutdown}
              showRamadan={showRamadan}
            />
          </div>
        </section>

        {program !== null && (
          <Section
            heading={strings.programs.requestsHeading}
            state={requests.kind}
            empty={requests.kind === "ready" && requests.data.items.length === 0}
            emptyBody={strings.programs.requestsEmpty}
          >
            {requests.kind === "ready" && requests.data.items.length > 0 && (
              <ul className="q-rfi-list" data-testid="program-requests">
                {requests.data.items.map((row) => (
                  <li key={row.id} className="q-rfi-card" data-testid="program-request-row">
                    <div className="q-rfi-card__top">
                      <Link to={`/rfis/${row.id}`} className="q-register__mono">
                        {row.humanId}
                      </Link>
                      <span className="q-chip">
                        {row.origin === "OPERATOR_REQUEST"
                          ? strings.programs.requestFromOperator
                          : strings.programs.requestFromContractor}
                      </span>
                    </div>
                    <p className="q-rfi-card__scope">{row.scope}</p>
                    <p className="q-rfi-card__meta">
                      {row.contractHumanId} · {row.contractorPartyName}
                      {row.disciplineLabel !== null && ` · ${row.disciplineLabel}`}
                    </p>
                    <p className="q-rfi-card__meta">
                      <strong>{day(row.plannedDate)}</strong> ·{" "}
                      {strings.rfis.dateState[row.dateState] ?? row.dateState}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        <Section
          heading={strings.programs.activitiesHeading}
          state={activities.kind}
          empty={activities.kind === "ready" && activitiesInLens.length === 0}
          emptyBody={strings.programs.lensEmpty}
        >
          {activities.kind === "ready" && activitiesInLens.length > 0 && (
            <div className="q-register-viewport">
              <table className="q-register">
                <thead>
                  <tr>
                    <th scope="col">{strings.schedules.columnActivity}</th>
                    <th scope="col">{strings.schedules.columnDate}</th>
                    <th scope="col">{strings.schedules.columnType}</th>
                    <th scope="col">{strings.schedules.columnContractor}</th>
                    <th scope="col">{strings.schedules.columnStatus}</th>
                  </tr>
                </thead>
                <tbody>
                  {activitiesInLens.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Link to={`/activities/${row.id}`} className="q-register__mono">
                          {row.humanId}
                        </Link>
                        {row.purpose !== null && (
                          <div className="q-register__wrap">{row.purpose}</div>
                        )}
                      </td>
                      <td>{day(row.plannedDate)}</td>
                      <td>{row.verificationTypeLabel}</td>
                      <td>{row.contractorPartyName}</td>
                      <td>
                        <StateChip state={row.status} label={row.statusLabel} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section
          heading={strings.programs.inspectionsHeading}
          state={inspections.kind}
          empty={inspections.kind === "ready" && inspectionsInLens.length === 0}
          emptyBody={strings.programs.lensEmpty}
        >
          {inspections.kind === "ready" && inspectionsInLens.length > 0 && (
            <div className="q-register-viewport">
              <table className="q-register">
                <thead>
                  <tr>
                    <th scope="col">{strings.inspections.columnRecord}</th>
                    <th scope="col">{strings.inspections.columnDate}</th>
                    <th scope="col">{strings.inspections.columnType}</th>
                    <th scope="col">{strings.inspections.columnContractor}</th>
                    <th scope="col">{strings.inspections.columnState}</th>
                  </tr>
                </thead>
                <tbody>
                  {inspectionsInLens.map((record: VerificationSummary) => (
                    <tr key={record.id}>
                      <td>
                        <Link to={`/inspections/${record.id}`} className="q-register__mono">
                          {record.humanId}
                        </Link>
                        <div className="q-register__wrap">{record.title}</div>
                      </td>
                      <td>{day(record.plannedDate)}</td>
                      <td>{record.verificationTypeLabel}</td>
                      <td>{record.contractorPartyName}</td>
                      <td>
                        <StateChip state={record.workflowState} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section
          heading={strings.programs.itpsHeading}
          state={itps.kind}
          empty={itps.kind === "ready" && itps.data.items.length === 0}
          emptyBody={strings.itps.emptyBody}
        >
          {itps.kind === "ready" && itps.data.items.length > 0 && (
            <div className="q-register-viewport">
              <table className="q-register">
                <thead>
                  <tr>
                    <th scope="col">{strings.itps.columnPlan}</th>
                    <th scope="col">{strings.itps.columnDiscipline}</th>
                    <th scope="col">{strings.itps.columnContractor}</th>
                    <th scope="col">{strings.itps.columnState}</th>
                  </tr>
                </thead>
                <tbody>
                  {itps.data.items.map((itp: ItpSummary) => (
                    <tr key={itp.id}>
                      <td>
                        <Link to={`/itps/${itp.id}`} className="q-register__mono">
                          {itp.humanId}
                        </Link>
                        <div className="q-register__wrap">{itp.title}</div>
                      </td>
                      <td>{itp.disciplineLabel}</td>
                      <td>{itp.contractorPartyName}</td>
                      <td>
                        <StateChip state={itp.workflowState} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
      {taxonomies.kind === "failed" && <span className="q-note">{strings.common.failed}</span>}
      {planning && planningProjects.kind === "ready" && (
        <CalendarPlanDialog
          projects={planningProjects.data}
          initialVerificationTypeCode={LENS_ACTIVITY_TYPE_CODES[lens][0]}
          onClose={() => {
            setPlanning(false);
          }}
          onPlanned={(message) => {
            setPlanning(false);
            setToast(message);
            setReload((n) => n + 1);
          }}
        />
      )}
      {creatingSchedule && planningProjects.kind === "ready" && (
        <CreateScheduleDialog
          projects={planningProjects.data}
          initialYear={year}
          onClose={() => {
            setCreatingSchedule(false);
          }}
          onCreated={(created) => {
            setCreatingSchedule(false);
            void navigate(`/schedules/${created.id}`);
          }}
        />
      )}
      {requesting && program !== null && targets.kind === "ready" && (
        <RequestInspectionDialog
          programCode={program}
          programTitle={title}
          targets={targets.data}
          disciplines={taxonomies.kind === "ready" ? taxonomies.data.disciplines : []}
          onClose={() => {
            setRequesting(false);
            if (actParam === "request") {
              const next = new URLSearchParams(searchParams);
              next.delete("act");
              setSearchParams(next, { replace: true });
            }
          }}
          onDone={(humanId) => {
            setRequesting(false);
            if (actParam === "request") {
              const next = new URLSearchParams(searchParams);
              next.delete("act");
              setSearchParams(next, { replace: true });
            }
            setToast(strings.programs.requestSent(humanId));
            setReload((n) => n + 1);
          }}
        />
      )}
    </>
  );
}

function Section({
  heading,
  state,
  empty,
  emptyBody,
  variant = "default",
  className,
  children,
}: {
  heading: string;
  state: "loading" | "ready" | "absence" | "failed";
  empty: boolean;
  emptyBody: string;
  variant?: "default" | "banded" | "paper";
  className?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const variantClass =
    variant === "banded" ? "q-section--banded" : variant === "paper" ? "q-section--paper" : "";
  return (
    <section
      className={`q-section ${variantClass} ${className ?? ""}`.trim()}
      data-variant={variant}
    >
      <h3 className="q-section__heading">{heading}</h3>
      {state === "loading" && <LoadingLine label={strings.common.loading} />}
      {state === "absence" && <AbsencePanel />}
      {state === "failed" && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {state === "ready" && empty && <EmptyState title={strings.common.empty} body={emptyBody} />}
      {state === "ready" && !empty && children}
    </section>
  );
}

/** The operator's request: scope, contract, discipline, proposed date, sent to the contractor. */
function RequestInspectionDialog({
  programCode,
  programTitle,
  targets,
  disciplines,
  onClose,
  onDone,
}: {
  readonly programCode: string;
  readonly programTitle: string;
  readonly targets: readonly RequestTarget[];
  readonly disciplines: readonly TaxonomyToken[];
  readonly onClose: () => void;
  readonly onDone: (humanId: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [contractId, setContractId] = useState(
    targets.length === 1 ? (targets[0]?.contractId ?? "") : "",
  );
  const [disciplineId, setDisciplineId] = useState("");
  const [scope, setScope] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [assigneePersonId, setAssigneePersonId] = useState("");
  const [instanceLabel, setInstanceLabel] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [externalAssetId, setExternalAssetId] = useState("");
  const [files, setFiles] = useState<readonly File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const withIds = disciplines.filter((one) => one.id !== undefined);
  // Wave RFI ruling 4 (b12 frontend item 7): the assignee is picked from the chosen
  // contract's team, once per person; the server judges eligibility (422
  // rfi.assignee_not_eligible names the person, said inside the dialog).
  const contract = useQuery(
    (signal) =>
      contractId === "" ? Promise.resolve(null) : getContract(token, contractId, signal),
    [token, contractId],
  );
  const people =
    contract.kind === "ready" && contract.data !== null
      ? contract.data.team.filter(
          (one, at, all) => all.findIndex((other) => other.personId === one.personId) === at,
        )
      : [];
  const missing: string[] = (
    [
      contractId === "" ? strings.programs.requestContract : null,
      disciplineId === "" ? strings.programs.requestDiscipline : null,
      scope.trim() === "" ? strings.programs.requestScope : null,
      date === "" ? strings.programs.requestDate : null,
    ] as (string | null)[]
  ).filter((one): one is string => one !== null);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const uploadedIds: string[] = [];
      for (const file of files) {
        const uploaded = await apiUpload(token, "/attachments/upload", file, (raw) => {
          const body = raw as Record<string, unknown>;
          return { id: String(body.id) };
        });
        uploadedIds.push(uploaded.id);
      }
      const sent = await requestInspection(token, {
        contract_id: contractId,
        program_code: programCode,
        discipline_id: disciplineId,
        scope: scope.trim(),
        proposed_date: date,
        location: location.trim() === "" ? null : location.trim(),
        attachment_ids: uploadedIds,
        instance_label: instanceLabel.trim() === "" ? null : instanceLabel.trim(),
        quantity: quantity.trim() === "" ? "1" : quantity.trim(),
        external_asset_id: externalAssetId.trim() === "" ? null : externalAssetId.trim(),
        ...(assigneePersonId.trim() !== "" ? { assignee_person_id: assigneePersonId.trim() } : {}),
      });
      onDone(sent.humanId);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={strings.programs.requestHeading(programTitle)}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.programs.requestLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="request-contract">
          {strings.programs.requestContract}
        </label>
        <ServerField field="contract_id">
          <NativeSelect
            id="request-contract"
            className={PICKER_CLASS}
            data-testid="request-contract"
            value={contractId}
            onChange={(event) => {
              setContractId(event.target.value);
            }}
          >
            <option value="">{strings.programs.requestChoose}</option>
            {targets.map((one) => (
              <PickerOption
                key={one.contractId}
                value={one.contractId}
                identifier={one.contractHumanId}
                name={
                  one.projectName !== null
                    ? `${one.contractorPartyName} · ${one.projectName}`
                    : one.contractorPartyName
                }
                width={pickerWidth(targets.map((t) => t.contractHumanId))}
              />
            ))}
          </NativeSelect>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="request-discipline">
          {strings.programs.requestDiscipline}
        </label>
        <ServerField field="discipline_id">
          <NativeSelect
            id="request-discipline"
            data-testid="request-discipline"
            value={disciplineId}
            onChange={(event) => {
              setDisciplineId(event.target.value);
            }}
          >
            <option value="">{strings.programs.requestChoose}</option>
            {withIds.map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </NativeSelect>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="request-scope">
          {strings.programs.requestScope}
        </label>
        <ServerField field="scope">
          <Textarea
            id="request-scope"
            data-testid="request-scope"
            value={scope}
            onChange={(event) => {
              setScope(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="request-date">
          {strings.programs.requestDate}
        </label>
        <ServerField field="proposed_date">
          <Input
            id="request-date"
            type="date"
            data-testid="request-date"
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="request-location">
          {strings.programs.requestLocation}
        </label>
        <ServerField field="location">
          <Input
            id="request-location"
            data-testid="request-location"
            value={location}
            onChange={(event) => {
              setLocation(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="request-instance">
          {strings.itps.rfiInstanceLabelOptional}
        </label>
        <p className="q-note" id="request-instance-hint">
          {strings.itps.rfiInstanceHintOptional}
        </p>
        <ServerField field="instance_label">
          <Input
            id="request-instance"
            data-testid="request-instance"
            aria-describedby="request-instance-hint"
            value={instanceLabel}
            onChange={(event) => {
              setInstanceLabel(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-form-grid">
        <div className="q-field">
          <label className="q-field__label" htmlFor="request-quantity">
            {strings.itps.rfiQuantityLabel}
          </label>
          <ServerField field="quantity">
            <Input
              id="request-quantity"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              data-testid="request-quantity"
              title={strings.itps.rfiQuantityHint}
              value={quantity}
              onChange={(event) => {
                setQuantity(event.target.value);
              }}
            />
          </ServerField>
        </div>
        <div className="q-field">
          <label className="q-field__label" htmlFor="request-asset">
            {strings.itps.rfiAssetLabel}
          </label>
          <ServerField field="external_asset_id">
            <Input
              id="request-asset"
              data-testid="request-asset"
              title={strings.itps.rfiAssetHint}
              value={externalAssetId}
              onChange={(event) => {
                setExternalAssetId(event.target.value);
              }}
            />
          </ServerField>
        </div>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="request-assignee">
          {strings.programs.requestAssignee}
        </label>
        <ServerField field="assignee_person_id">
          <NativeSelect
            id="request-assignee"
            className={PICKER_CLASS}
            data-testid="request-assignee"
            disabled={contractId === ""}
            value={assigneePersonId}
            onChange={(event) => {
              setAssigneePersonId(event.target.value);
            }}
          >
            <option value="">{strings.programs.requestAssigneeNone}</option>
            {people.map((one) => (
              <option key={one.personId} value={one.personId}>
                {strings.programs.requestAssigneeOption(one.displayName, one.roleName)}
              </option>
            ))}
          </NativeSelect>
        </ServerField>
        <p className="q-note">{strings.programs.requestAssigneeHint}</p>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="request-attachments">
          {strings.programs.requestAttachments}
        </label>
        <ServerField field="attachment_ids">
          <FilePicker
            files={files}
            onChange={setFiles}
            label={strings.programs.requestAttachments}
            testId="request-attachments"
            emptyText={strings.programs.requestAttachmentsEmpty}
          />
        </ServerField>
      </div>
      {missing.length > 0 && (
        <p className="q-note" data-testid="request-missing">
          {strings.programs.requestMissing(missing)}
        </p>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="request-send"
          disabled={busy || missing.length > 0}
          onClick={() => {
            void send();
          }}
        >
          {strings.programs.requestSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
